import classNames from 'classnames';
import { message } from 'antd';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  type DragCancelEvent,
  type DragEndEvent,
  type DragStartEvent,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ExportOutlined } from '@ant-design/icons';
import { GripHandleIcon, MenuDots } from '../../../../Icons';
import { NowPlayingLayout } from '../layout';
import { usePlayback } from '../../../../../contexts/PlaybackContext';
import QueueListItem from './QueueListItem';
import { LibraryPauseGlyph, LibraryPlayGlyph } from '../../../../../components/Layout/components/Library/list/ListCards';
import { RecordingActionsWrapper } from '../../../../../pages/Recording/components/RecordingActions';
import type { LibraryRecording } from '../../../../../interfaces/recording';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import { playlistService } from '../../../../../services/playlists';
import {
  fetchPreferences,
  preferencesSelectors,
  removePreference,
  upsertPreference
} from '../../../../../store/slices/preferences';
import {
  buildQueueListEntries,
  deriveQueuePointer,
  formatQueueDuration
} from '../../../../../lib/playback/queueDisplay';
import type { QueueListEntry } from '../../../../../lib/playback/queueDisplay';
import type { GroupedQueue, QueueRecordingGroup } from './queueTypes';
import {
  buildQueueRenderableItems,
  deriveDraggableTrackOrder,
  mergeTrackOrder,
  reorderIds
} from './queueReorder';
import { isVerboseLoggingEnabled } from '../../../../../lib/debug/logging';
import { onboardingActions, selectOnboardingFlag } from '../../../../../store/slices/onboarding';
import { isDevUser } from '../../../../../lib/config/devUsers';
import { isMixExportAllowedUser } from '../../../../../lib/config/mixExportAllowlist';

const verboseLoggingEnabled = isVerboseLoggingEnabled();

const logQueueDrag = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== 'production' && verboseLoggingEnabled) {
    console.debug('[Queue DnD]', ...args);
  }
};

const QUEUE_LOG_PREFIX = '[Queue Artists]';
const logQueueArtists = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== 'production' && verboseLoggingEnabled) {
    console.debug(QUEUE_LOG_PREFIX, ...args);
  }
};

const MIX_PREF_TOOLTIP_KEY = 'mix_pref_tooltip';

const showQueueSummaryChips = process.env.NEXT_PUBLIC_SHOW_QUEUE_SUMMARY_CHIPS === 'true';
const clampPreferenceWeight = (value: number): number => {
  if (!Number.isFinite(value)) return 1;
  return value;
};

type PendingRemoval =
  | { type: 'item'; id: string }
  | { type: 'group'; id: string };

type DragData =
  | { type: 'group'; groupId: string; canDrag: boolean }
  | { type: 'track'; trackId: string; groupId: string; canDrag: boolean };

type ActiveDragState =
  | { type: 'group'; group: QueueRecordingGroup }
  | { type: 'track'; entry: QueueListEntry; groupId: string };

const abbreviateComposerName = (fullName: string | null): string | null => {
  if (!fullName) return null;
  const parts = fullName
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];

  const lastName = parts[parts.length - 1];
  const leadingParts = parts.slice(0, -1);

  const particles: string[] = [];
  const coreParts: string[] = [];

  for (const part of leadingParts) {
    if (/^[a-z]/.test(part)) {
      particles.push(part);
    } else {
      coreParts.push(part);
    }
  }

  const abbreviateSegment = (segment: string): string =>
    segment
      .split('-')
      .filter((value) => value.length > 0)
      .map((value) => `${value[0]?.toUpperCase() ?? ''}.`)
      .join('-');

  const initials = coreParts.map(abbreviateSegment).join('');
  const abbreviateParticle = (segment: string): string =>
    segment
      .split('-')
      .filter((value) => value.length > 0)
      .map((value) => `${value[0]?.toUpperCase() ?? ''}.`)
      .join('-');
  const particleInitials = particles.map(abbreviateParticle).join('');
  const combinedInitials = `${initials}${particleInitials}`;

  if (!combinedInitials) {
    const particlePortion = particles.join(' ');
    return [particlePortion, lastName].filter(Boolean).join(' ').trim();
  }

  return `${combinedInitials} ${lastName}`.trim();
};

const formatCatalogKey = (catalog: string | null): string | null => {
  if (!catalog) return null;
  const trimmed = catalog.trim();
  if (!trimmed) return null;

  const [prefix, ...restParts] = trimmed.split(/\s+/);
  if (!prefix) {
    return trimmed;
  }

  const normalizedPrefix = prefix.replace(/[a-z]/g, (char) => char.toUpperCase());
  if (restParts.length === 0) {
    return normalizedPrefix;
  }

  return [normalizedPrefix, ...restParts].join(' ');
};

const QueueHeaderPlaceholderButton = ({
  onClick,
  loading
}: {
  onClick: () => void;
  loading?: boolean;
}) => {
  const authUser = useAppSelector((state) => state.auth.user);
  const userId = authUser?.id ?? null;
  const isAllowed = isMixExportAllowedUser(userId);

  if (!isAllowed) {
    return null;
  }

  return (
    <div className='queue-header-placeholder-button'>
      <button
        type='button'
        aria-label='Export current mix snapshot'
        onClick={onClick}
        disabled={loading}
      >
        <ExportOutlined />
      </button>
    </div>
  );
};

export const Queue = () => {
  const dispatch = useAppDispatch();
  const {
    queueSession,
    queueItems,
    queueStatus,
    queueError,
    performQueueAction,
    resumeTrack,
    pauseTrack,
    isPlaying
  } = usePlayback();
  const preferencesStatus = useAppSelector(preferencesSelectors.selectPreferencesStatus);
  const shouldRefreshPreferences = useAppSelector(
    preferencesSelectors.selectPreferencesShouldRefresh
  );

  useEffect(() => {
    if (preferencesStatus === 'loading') return;
    if (preferencesStatus === 'idle' || shouldRefreshPreferences) {
      dispatch(fetchPreferences()).catch(() => {});
    }
  }, [dispatch, preferencesStatus, shouldRefreshPreferences]);

  const pointer = useMemo(() => deriveQueuePointer(queueSession, queueItems), [queueItems, queueSession]);
  const entries = useMemo(() => buildQueueListEntries(queueItems, pointer.index), [queueItems, pointer.index]);
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(false);
  const [scrollRecordingId, setScrollRecordingId] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [optimisticGroupOrder, setOptimisticGroupOrder] = useState<string[] | null>(null);
  const [optimisticTrackOrders, setOptimisticTrackOrders] = useState<Record<string, string[]> | null>(null);
  const [activeDragState, setActiveDragState] = useState<ActiveDragState | null>(null);
  const [moveNextPendingGroupId, setMoveNextPendingGroupId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const authUserId = useAppSelector((state) => state.auth.user?.id ?? null);

  const visibleEntries = useMemo(
    () => entries.filter((entry) => !(entry.isPast && !entry.isSameRecording)),
    [entries]
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const pointerRecordingId =
    pointer.item && pointer.item.groupType === 'recording'
      ? pointer.item.groupId ??
        (typeof pointer.item.metadata?.recordingId === 'string' ? pointer.item.metadata.recordingId : null)
      : null;

  const totalItems = queueItems.length;
  const hasSession = Boolean(queueSession);
  const currentPosition = pointer.index != null ? pointer.index + 1 : null;
  const shuffleEnabled = queueSession?.shuffleEnabled ?? false;
  const shuffleSeed = queueSession?.shuffleSeed ?? null;
  const revision = queueSession?.revision ?? null;
  const isCustomMixSession = useMemo(() => {
    if (!queueSession?.sourceSnapshot) return false;
    const snapshot = queueSession.sourceSnapshot as Record<string, unknown>;
    const strategy = typeof snapshot.strategy === 'string' ? snapshot.strategy : null;
    if (strategy === 'custom-mix') {
      return true;
    }
    const seed = snapshot.seed;
    if (seed && typeof seed === 'object' && (seed as { type?: unknown }).type === 'custom-mix') {
      return true;
    }
    return false;
  }, [queueSession]);
  const mixName = useMemo(() => {
    const snapshot = queueSession?.sourceSnapshot as Record<string, unknown> | undefined;
    const raw =
      snapshot && typeof snapshot.mixName === 'string' && snapshot.mixName.trim().length > 0
        ? snapshot.mixName.trim()
        : '';
    return raw || 'Custom Mix';
  }, [queueSession]);

  const handleSelect = useCallback(
    (targetIndex: number) => {
      if (!queueSession || queueStatus !== 'ready') {
        return;
      }
      if (pointer.index === targetIndex) {
        return;
      }
      void performQueueAction('jump-to-index', { targetIndex });
    },
    [performQueueAction, pointer.index, queueSession, queueStatus]
  );

  const isListInteractive = queueStatus === 'ready' && !!queueSession;
  const listRef = useRef<HTMLUListElement | null>(null);
  const lastPointerScrollRef = useRef<{ index: number; recordingId: string | null } | null>(null);

  const exportCurrentMixSnapshot = useCallback(async () => {
    if (!isMixExportAllowedUser(authUserId)) {
      console.info('[MixExport] blocked: user not allowlisted', { authUserId });
      void message.info('Export is only enabled for the beta allowlist.');
      return;
    }
    console.info('[MixExport] start', {
      authUserId,
      hasSession: !!queueSession,
      queueStatus,
      isCustomMixSession,
      itemCount: queueItems.length
    });
    if (!queueSession || queueStatus !== 'ready') {
      void message.info('Build a mix and start playback before exporting.');
      return;
    }
    if (!isCustomMixSession) {
      void message.info('Export is only available for Custom Mix sessions.');
      return;
    }
    const sortedItems = [...queueItems].sort((a, b) => a.position - b.position);
    const uris = sortedItems
      .map((item) => {
        const raw =
          (typeof item.resolvedTrackUri === 'string' ? item.resolvedTrackUri : '') ||
          (typeof item.trackUri === 'string' ? item.trackUri : '');
        return raw?.trim();
      })
      .filter((uri): uri is string => !!uri && uri.length > 0);
    if (!uris.length) {
      void message.error('No tracks available to export.');
      return;
    }
    if (!authUserId) {
      void message.error('Need Spotify user information to create a playlist.');
      return;
    }

    const pad = (value: number): string => value.toString().padStart(2, '0');
    const now = new Date();
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const playlistName = `${mixName} — snapshot ${stamp}`;
    const description = `Static playlist snapshot exported from Custom Mix "${mixName}".`;

    setExporting(true);
    try {
      const createResponse = await playlistService.createPlaylist(authUserId, {
        name: playlistName,
        public: false,
        description
      });
      const playlist = createResponse.data;
      try {
        // Ensure playlist is private even if the API defaults to public.
        await playlistService.changePlaylistDetails(playlist.id, { public: false, collaborative: false });
      } catch (privacyError) {
        console.warn('[MixExport] failed to enforce private playlist', privacyError);
      }
      let snapshotId = playlist.snapshot_id;
      const chunkSize = 100;
      for (let offset = 0; offset < uris.length; offset += chunkSize) {
        const chunk = uris.slice(offset, offset + chunkSize);
        const addResponse = await playlistService.addPlaylistItems(playlist.id, chunk, snapshotId);
        const nextSnapshotId =
          addResponse?.data &&
          typeof (addResponse.data as { snapshot_id?: unknown }).snapshot_id === 'string'
            ? (addResponse.data as { snapshot_id: string }).snapshot_id
            : null;
        if (nextSnapshotId) {
          snapshotId = nextSnapshotId;
        }
      }
      console.info('[MixExport] success', { playlistId: playlist.id, trackCount: uris.length });
      void message.success('Exported current mix snapshot to Spotify.');
    } catch (error) {
      console.error('Failed to export mix snapshot', error);
      void message.error('Unable to export mix snapshot right now.');
    } finally {
      setExporting(false);
    }
  }, [authUserId, isCustomMixSession, mixName, queueItems, queueSession, queueStatus]);

  const handleRemoveEntry = useCallback(
    async (entry: QueueListEntry) => {
      if (!queueSession || queueStatus !== 'ready' || pendingRemoval) {
        return;
      }

      const queueItem = queueItems[entry.queueIndex];
      const targetItemId = queueItem?.id ?? entry.id;
      if (!targetItemId) {
        return;
      }

      setPendingRemoval({ type: 'item', id: targetItemId });

      try {
        await performQueueAction('remove-items', { targetItemIds: [targetItemId] });
      } catch (error) {
        void message.error('Failed to remove item from queue');
      } finally {
        setPendingRemoval(null);
      }
    },
    [pendingRemoval, performQueueAction, queueItems, queueSession, queueStatus]
  );

  const handleRemoveGroup = useCallback(
    async (group: QueueRecordingGroup) => {
      if (!queueSession || queueStatus !== 'ready' || !group.hasRemovableTracks || pendingRemoval) {
        return;
      }

      const targetGroupId = group.targetGroupId ?? group.id;
      if (!targetGroupId) {
        return;
      }

      setPendingRemoval({ type: 'group', id: targetGroupId });

      try {
        await performQueueAction('remove-group', { targetGroupId });
      } catch (error) {
        void message.error('Failed to remove recording from queue');
      } finally {
        setPendingRemoval(null);
      }
    },
    [pendingRemoval, performQueueAction, queueSession, queueStatus]
  );

  const handlePlayGroup = useCallback(
    (group: QueueRecordingGroup) => {
      if (!isListInteractive) {
        return;
      }
      if (group.hasCurrentTrack) {
        if (isPlaying) {
          void pauseTrack();
        } else {
          void resumeTrack();
        }
        return;
      }
      const upcomingTrack = group.tracks.find((track) => !track.isPast);
      const targetEntry = upcomingTrack ?? group.tracks[0];
      if (!targetEntry) {
        return;
      }
      const targetIndex = targetEntry.queueIndex;
      if (typeof targetIndex !== 'number') {
        return;
      }
      void performQueueAction('jump-to-index', { targetIndex });
    },
    [isListInteractive, isPlaying, pauseTrack, performQueueAction, resumeTrack]
  );

  const emptyMessage: ReactNode =
    queueStatus === 'hydrating' ? (
      <span className='queue-empty__dots' role='status' aria-label='Loading queue'>
        <span className='queue-empty__dot' />
        <span className='queue-empty__dot' />
        <span className='queue-empty__dot' />
      </span>
    ) : hasSession ? (
      'Queue has no items yet.'
    ) : null;

  useEffect(() => {
    const scrollContainer = listRef.current;

    if (!scrollContainer) {
      return;
    }

    if (pointer.index == null) {
      lastPointerScrollRef.current = null;
      return;
    }

    const snapshot = lastPointerScrollRef.current;
    if (snapshot && snapshot.index === pointer.index && snapshot.recordingId === pointerRecordingId) {
      return;
    }

    let target: HTMLElement | null = null;

    if (pointerRecordingId) {
      const headerNodes = scrollContainer.querySelectorAll<HTMLElement>('[data-queue-recording-id]');
      target =
        Array.from(headerNodes).find((node) => node.dataset.queueRecordingId === pointerRecordingId) ?? null;
    }

    if (!target) {
      target = scrollContainer.querySelector<HTMLElement>('[data-testid="queue-current"]');
    }

    if (!target) {
      return;
    }

    lastPointerScrollRef.current = { index: pointer.index, recordingId: pointerRecordingId ?? null };

    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const offset = targetRect.top - containerRect.top + scrollContainer.scrollTop;

    scrollContainer.scrollTo({
      top: Math.max(offset - 12, 0)
    });
  }, [pointer.index, pointerRecordingId, visibleEntries.length]);

  useEffect(() => {
    if (!scrollRecordingId) {
      return;
    }
    const scrollContainer = listRef.current;
    if (!scrollContainer) {
      return;
    }

    const header = scrollContainer.querySelector<HTMLElement>(
      `[data-queue-recording-id="${scrollRecordingId}"]`
    );

    if (!header) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = header.getBoundingClientRect();
    const offset = targetRect.top - containerRect.top + scrollContainer.scrollTop;

    scrollContainer.scrollTo({
      top: Math.max(offset - 12, 0)
    });

    setScrollRecordingId(null);
  }, [scrollRecordingId, visibleEntries.length]);

  const canonicalGrouped = useMemo<GroupedQueue>(() => {
    const recordings = new Map<string, QueueRecordingGroup>();
    const groupOrder: string[] = [];

    for (const entry of entries) {
      const isRecording = entry.groupType === 'recording' && (entry.groupId || entry.metadata.recordingId);
      const groupId = isRecording
        ? entry.groupId ?? entry.metadata.recordingId ?? entry.id
        : entry.id;

      let group = recordings.get(groupId);
      if (!group) {
        const meta = entry.metadata;
        const composerName = meta.composerName ?? null;
        const rawPerformer = meta.performerName ?? meta.artists ?? null;
        const performerName = (() => {
          if (!rawPerformer) return null;
          const composer = composerName?.toLowerCase().trim();
          const parts = rawPerformer
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
            .filter((value) => (composer ? value.toLowerCase() !== composer : true));
          if (parts.length === 0) {
            return composer ? null : rawPerformer;
          }
          return parts.join(', ');
        })();

        const initialDuration =
          (typeof meta.totalDurationMs === 'number' && Number.isFinite(meta.totalDurationMs)
            ? meta.totalDurationMs
            : null) ??
          (typeof entry.durationMs === 'number' && Number.isFinite(entry.durationMs)
            ? entry.durationMs
            : null) ??
          null;

        group = {
          id: groupId,
          isManual: !isRecording,
          label: meta.recordingName ?? meta.albumName ?? meta.title ?? 'Recording',
          workName: meta.workName ?? null,
          catalogKey: meta.catalogKey ?? meta.catalogNumberNormalized ?? null,
          composerName,
          composerId: meta.composerId ?? null,
          performerName,
          albumName: meta.albumName ?? null,
          albumImageUrl: meta.albumImageUrl ?? null,
          releaseYear: meta.releaseYear ?? null,
          trackCount: isRecording ? meta.trackCount ?? 0 : 1,
          totalDurationMs: initialDuration,
          durationLabel: initialDuration != null ? formatQueueDuration(initialDuration) : null,
          hasCurrentTrack: false,
          currentTrackTitle: null,
          tracks: [],
          explicitTrackCount: isRecording ? meta.trackCount != null : true,
          explicitDuration: isRecording ? meta.totalDurationMs != null : true,
          derivedDurationMs: 0,
          hasRemovableTracks: !entry.isPast,
          targetGroupId: isRecording ? entry.groupId ?? entry.metadata.recordingId ?? null : groupId
        };
        recordings.set(groupId, group);
        groupOrder.push(groupId);
      }

      group.tracks.push(entry);
      if (!group.hasRemovableTracks && !entry.isPast) {
        group.hasRemovableTracks = true;
      }
      if (!group.targetGroupId && entry.groupId) {
        group.targetGroupId = entry.groupId;
      }
      if (entry.isCurrent) {
        group.currentTrackTitle = entry.metadata.title ?? `Track ${entry.position}`;
      }
      if (entry.isCurrent || entry.isSameRecording) {
        group.hasCurrentTrack = true;
      }
      if (!group.explicitTrackCount) {
        group.trackCount = group.tracks.length;
      }
      if (!group.explicitDuration && typeof entry.durationMs === 'number' && Number.isFinite(entry.durationMs)) {
        group.derivedDurationMs += entry.durationMs;
        group.totalDurationMs = group.derivedDurationMs;
        group.durationLabel = formatQueueDuration(group.derivedDurationMs);
      }
    }

    return { recordings, groupOrder };
  }, [entries]);

  const visibleQueueIndexes = useMemo(() => new Set(visibleEntries.map((entry) => entry.queueIndex)), [visibleEntries]);

  const grouped = useMemo<GroupedQueue>(() => {
    const recordings = new Map<string, QueueRecordingGroup>();
    const groupOrder: string[] = [];

    for (const groupId of canonicalGrouped.groupOrder) {
      const group = canonicalGrouped.recordings.get(groupId);
      if (!group) continue;
      const filteredTracks = group.tracks.filter((track) => visibleQueueIndexes.has(track.queueIndex));
      if (!filteredTracks.length) continue;
      const hasRemovableTracks = group.hasRemovableTracks && filteredTracks.some((track) => !track.isPast);
      recordings.set(groupId, { ...group, tracks: filteredTracks, hasRemovableTracks });
      groupOrder.push(groupId);
    }

    return { recordings, groupOrder };
  }, [canonicalGrouped, visibleQueueIndexes]);

  const effectiveGroupOrder = useMemo(
    () =>
      (optimisticGroupOrder && optimisticGroupOrder.length
        ? optimisticGroupOrder
        : grouped.groupOrder
      ).filter((groupId) => grouped.recordings.has(groupId)),
    [grouped, optimisticGroupOrder]
  );

  const currentRecordingGroupId = useMemo(() => {
    if (!pointerRecordingId) {
      return null;
    }
    return grouped.recordings.has(pointerRecordingId) ? pointerRecordingId : null;
  }, [grouped, pointerRecordingId]);

  const nextRecordingGroupId = useMemo(() => {
    if (!currentRecordingGroupId) {
      return null;
    }
    const currentIndex = effectiveGroupOrder.indexOf(currentRecordingGroupId);
    if (currentIndex === -1) {
      return null;
    }
    return effectiveGroupOrder[currentIndex + 1] ?? null;
  }, [currentRecordingGroupId, effectiveGroupOrder]);

  useEffect(() => {
    if (!pointerRecordingId) {
      return;
    }
    setAllGroupsExpanded(true);
  }, [pointerRecordingId]);

  useEffect(() => {
    setOptimisticGroupOrder(null);
    setOptimisticTrackOrders(null);
    setActiveDragState(null);
    setMoveNextPendingGroupId(null);
  }, [queueItems]);

  const toggleGroupExpansion = useCallback((groupId: string) => {
    setAllGroupsExpanded((prev) => {
      const next = !prev;
      setScrollRecordingId(next ? groupId : null);
      return next;
    });
  }, []);

  const renderPlan = useMemo(
    () =>
      buildQueueRenderableItems({
        grouped,
        groupOrderOverride: optimisticGroupOrder,
        trackOrderOverrides: optimisticTrackOrders
      }),
    [grouped, optimisticGroupOrder, optimisticTrackOrders]
  );

  const groupSortableIds = useMemo(
    () =>
      effectiveGroupOrder
        .map((id) => grouped.recordings.get(id))
        .filter((group): group is QueueRecordingGroup => Boolean(group))
        .filter((group) => group.hasRemovableTracks)
        .map((group) => `group:${group.id}`),
    [effectiveGroupOrder, grouped]
  );

  const resolveQueueItemId = useCallback(
    (entry: QueueListEntry) => queueItems[entry.queueIndex]?.id ?? entry.id,
    [queueItems]
  );

  const getGroupById = useCallback(
    (groupId: string) => grouped.recordings.get(groupId) ?? null,
    [grouped]
  );

  const getCanonicalGroupById = useCallback(
    (groupId: string) => canonicalGrouped.recordings.get(groupId) ?? null,
    [canonicalGrouped]
  );

  const handleMoveGroupNext = useCallback(
    async (group: QueueRecordingGroup) => {
      if (!isListInteractive) {
        return;
      }
      if (!currentRecordingGroupId) {
        return;
      }

      const baseOrder = effectiveGroupOrder;
      const sourceIndex = baseOrder.indexOf(group.id);
      const currentIndex = baseOrder.indexOf(currentRecordingGroupId);

      if (sourceIndex === -1 || currentIndex === -1) {
        return;
      }

      const targetIndex = Math.min(
        sourceIndex < currentIndex ? currentIndex : currentIndex + 1,
        baseOrder.length - 1
      );

      if (targetIndex === sourceIndex) {
        return;
      }

      const previousOrder = [...baseOrder];
      const nextOrder = reorderIds(baseOrder, sourceIndex, targetIndex);

      setOptimisticGroupOrder(nextOrder);
      setMoveNextPendingGroupId(group.id);

      try {
        await performQueueAction('move-group', {
          groupId: group.targetGroupId ?? group.id,
          targetGroupIndex: targetIndex,
        });
      } catch (error) {
        setOptimisticGroupOrder(previousOrder);
        void message.error('Failed to move recording');
      } finally {
        setMoveNextPendingGroupId((prev) => (prev === group.id ? null : prev));
      }
    },
    [
      currentRecordingGroupId,
      effectiveGroupOrder,
      isListInteractive,
      performQueueAction
    ]
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current as DragData | undefined;
      if (!data || !data.canDrag) {
        logQueueDrag('start ignored', {
          id: event.active.id,
          reason: 'not-draggable'
        });
        setActiveDragState(null);
        return;
      }
      logQueueDrag('start', {
        id: event.active.id,
        type: data.type,
        groupId: data.type === 'group' ? data.groupId : data.groupId,
        trackId: data.type === 'track' ? data.trackId : undefined
      });

      if (data.type === 'group') {
        const group = getGroupById(data.groupId);
        if (group) {
          setActiveDragState({ type: 'group', group });
        }
        return;
      }

      if (data.type === 'track') {
        const group = getGroupById(data.groupId);
        if (!group) {
          setActiveDragState(null);
          return;
        }
        const entry = group.tracks.find((track) => track.id === data.trackId);
        if (entry) {
          setActiveDragState({ type: 'track', entry, groupId: group.id });
        } else {
          setActiveDragState(null);
        }
      }
    },
    [getGroupById]
  );

  const handleDragCancel = useCallback(
    (_event: DragCancelEvent) => {
      logQueueDrag('cancel');
      setActiveDragState(null);
    },
    []
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const activeData = event.active.data.current as DragData | undefined;
      const overData = event.over?.data.current as DragData | undefined;
      logQueueDrag('end', {
        activeId: event.active.id,
        overId: event.over?.id ?? null,
        activeType: activeData?.type ?? null,
        overType: overData?.type ?? null
      });
      setActiveDragState(null);

      if (!activeData || !activeData.canDrag) {
        return;
      }

      if (activeData.type === 'group') {
        if (!overData || overData.type !== 'group') {
          return;
        }

        const currentOrder =
          optimisticGroupOrder && optimisticGroupOrder.length > 0
            ? optimisticGroupOrder
            : canonicalGrouped.groupOrder;
        const previousOrderSnapshot = [...currentOrder];
        const fromIndex = currentOrder.indexOf(activeData.groupId);
        const toIndex = currentOrder.indexOf(overData.groupId);

        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
          return;
        }

        const group = getCanonicalGroupById(activeData.groupId);
        if (!group) {
          return;
        }

        const payloadGroupId = group.targetGroupId ?? group.id;
        const hadOverride = Boolean(optimisticGroupOrder && optimisticGroupOrder.length > 0);
        const nextOrder = reorderIds(currentOrder, fromIndex, toIndex);
        setOptimisticGroupOrder(nextOrder);

        logQueueDrag('submit move-group', {
          groupId: payloadGroupId,
          fromIndex,
          toIndex
        });

        void performQueueAction('move-group', {
          groupId: payloadGroupId,
          targetGroupIndex: toIndex,
        })
          .then(() => {
            logQueueDrag('complete move-group', {
              groupId: payloadGroupId,
              fromIndex,
              toIndex
            });
          })
          .catch((error) => {
            setOptimisticGroupOrder(hadOverride ? [...previousOrderSnapshot] : null);
            void message.error('Failed to reorder recordings');
            logQueueDrag('error move-group', {
              groupId: payloadGroupId,
              error
            });
          });
        return;
      }

      if (activeData.type === 'track') {
        if (
          !overData ||
          overData.type !== 'track' ||
          overData.groupId !== activeData.groupId
        ) {
          return;
        }

        const group = getCanonicalGroupById(activeData.groupId);
        if (!group) {
          return;
        }

        const previousOverride = optimisticTrackOrders?.[group.id] ?? null;
        const baseOrder = previousOverride ?? deriveDraggableTrackOrder(group);
        const fromIndex = baseOrder.indexOf(activeData.trackId);
        const toIndex = baseOrder.indexOf(overData.trackId);

        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
          return;
        }

        const payloadGroupId = group.targetGroupId ?? group.id;
        const nextOrder = reorderIds(baseOrder, fromIndex, toIndex);
        setOptimisticTrackOrders((prev) => mergeTrackOrder(prev, group.id, nextOrder));

        logQueueDrag('submit move-track', {
          groupId: payloadGroupId,
          trackId: activeData.trackId,
          fromIndex,
          toIndex
        });

        void performQueueAction('move-track', {
          groupId: payloadGroupId,
          sourceIndex: fromIndex,
          targetIndex: toIndex,
        })
          .then(() => {
            logQueueDrag('complete move-track', {
              groupId: payloadGroupId,
              trackId: activeData.trackId,
              fromIndex,
              toIndex
            });
          })
          .catch((error) => {
            setOptimisticTrackOrders((prev) => {
              if (previousOverride) {
                return mergeTrackOrder(prev, group.id, [...previousOverride]);
              }
              if (!prev) {
                return null;
              }
              const { [group.id]: _, ...rest } = prev;
              return Object.keys(rest).length ? rest : null;
            });
            void message.error('Failed to reorder tracks');
            logQueueDrag('error move-track', {
              groupId: payloadGroupId,
              trackId: activeData.trackId,
              error
            });
          });
      }
    },
    [canonicalGrouped, getCanonicalGroupById, optimisticGroupOrder, optimisticTrackOrders, performQueueAction]
  );

  return (
    <NowPlayingLayout
      title='Queue'
      titleExtra={<QueueHeaderPlaceholderButton onClick={exportCurrentMixSnapshot} loading={exporting} />}
    >
      <div className='queue-panel'>
        <div className='queue-summary'>
          {showQueueSummaryChips ? (
            <div className='queue-summary__metrics'>
              <span data-testid='queue-total'>Items: {totalItems}</span>
              {currentPosition != null ? (
                <span data-testid='queue-position'>
                  Position: {currentPosition} / {totalItems || '—'}
                </span>
              ) : null}
              <span data-testid='queue-shuffle'>
                Shuffle: {shuffleEnabled ? 'On' : 'Off'}
                {shuffleEnabled && shuffleSeed ? ` • ${shuffleSeed.slice(0, 8)}` : ''}
              </span>
              {revision != null ? <span data-testid='queue-revision'>Revision: #{revision}</span> : null}
            </div>
          ) : null}
          {queueError ? <div className='queue-summary__error'>{queueError}</div> : null}
        </div>

        <div className='queue-panel__body'>
          {visibleEntries.length > 0 ? (
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={groupSortableIds} strategy={verticalListSortingStrategy}>
                <ul className='queue-list' ref={listRef}>
                  {renderPlan.map((item) => {
                    const group = item.group;
                    if (group.isManual) {
                      return (
                        <SortableManualQueueItem
                          key={`group-${group.id}`}
                          group={group}
                          isInteractive={isListInteractive}
                          pendingRemoval={pendingRemoval}
                          resolveItemId={resolveQueueItemId}
                          onRemove={handleRemoveEntry}
                          onSelect={handleSelect}
                        />
                      );
                    }

                    return (
                      <QueueGroupRow
                        key={`group-${group.id}`}
                        group={group}
                        isExpanded={allGroupsExpanded}
                        isInteractive={isListInteractive}
                        pendingRemoval={pendingRemoval}
                        onToggleExpansion={toggleGroupExpansion}
                        onRemoveGroup={handleRemoveGroup}
                        onRemoveEntry={handleRemoveEntry}
                        onSelectEntry={handleSelect}
                        resolveItemId={resolveQueueItemId}
                        onPlayGroup={handlePlayGroup}
                        onMoveGroupNext={handleMoveGroupNext}
                        currentRecordingGroupId={currentRecordingGroupId}
                        nextRecordingGroupId={nextRecordingGroupId}
                        moveNextPendingGroupId={moveNextPendingGroupId}
                        isPlaying={isPlaying}
                      />
                    );
                  })}
                </ul>
              </SortableContext>
              <DragOverlay>
                {activeDragState ? (
                  activeDragState.type === 'group' ? (
                    <QueueDragOverlay kind='group' group={activeDragState.group} />
                  ) : (
                    <QueueDragOverlay kind='track' entry={activeDragState.entry} />
                  )
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : emptyMessage ? (
            <div className='queue-empty' data-testid='queue-empty-state'>
              {emptyMessage}
            </div>
          ) : null}
        </div>
      </div>
    </NowPlayingLayout>
  );
};

type QueueGroupRowProps = {
  group: QueueRecordingGroup;
  isExpanded: boolean;
  isInteractive: boolean;
  pendingRemoval: PendingRemoval | null;
  onToggleExpansion: (groupId: string) => void;
  onRemoveGroup: (group: QueueRecordingGroup) => void;
  onRemoveEntry: (entry: QueueListEntry) => void;
  onSelectEntry: (index: number) => void;
  resolveItemId: (entry: QueueListEntry) => string;
  onPlayGroup: (group: QueueRecordingGroup) => void;
  onMoveGroupNext: (group: QueueRecordingGroup) => void;
  currentRecordingGroupId: string | null;
  nextRecordingGroupId: string | null;
  moveNextPendingGroupId: string | null;
  isPlaying: boolean;
};

const deriveGroupArtists = (group: QueueRecordingGroup) => {
  const composerId = group.composerId?.trim() ?? null;
  const composerNameKey = group.composerName?.trim().toLowerCase() ?? null;
  const byId = new Map<string, string | null>();

  for (const track of group.tracks) {
    const meta = track.metadata as Record<string, unknown>;

    const credits = Array.isArray(meta.artistCredits) ? meta.artistCredits : [];
    for (const credit of credits as Array<Record<string, unknown>>) {
      const rawId = credit?.id;
      const id = typeof rawId === 'string' && rawId.trim().length > 0 ? rawId.trim() : null;
      const rawName = credit?.name;
      const name = typeof rawName === 'string' && rawName.trim().length > 0 ? rawName.trim() : null;
      if (id) {
        if (composerId && id === composerId) continue;
        if (composerNameKey && name && name.toLowerCase() === composerNameKey) continue;
        const existing = byId.get(id);
        if (!existing) {
          byId.set(id, name);
        } else if (!existing && name) {
          byId.set(id, name);
        }
      }
    }

    const artistIds = Array.isArray(meta.artistIds) ? meta.artistIds : [];
    for (const rawId of artistIds) {
      const id = typeof rawId === 'string' && rawId.trim().length > 0 ? rawId.trim() : null;
      if (!id) continue;
      if (composerId && id === composerId) continue;
      if (!byId.has(id)) {
        byId.set(id, null);
      }
    }
  }

  const artists = Array.from(byId.entries()).map(([id, name]) => ({ id, name: name ?? undefined }));
  logQueueArtists(group.id, { count: artists.length, composerId, composerNameKey, sample: artists.slice(0, 5) });
  return artists;
};

const QueueGroupRow = ({
  group,
  isExpanded,
  isInteractive,
  pendingRemoval,
  onToggleExpansion,
  onRemoveGroup,
  onRemoveEntry,
  onSelectEntry,
  resolveItemId,
  onPlayGroup,
  onMoveGroupNext,
  currentRecordingGroupId,
  nextRecordingGroupId,
  moveNextPendingGroupId,
  isPlaying
}: QueueGroupRowProps) => {
  const dispatch = useAppDispatch();
  const preferencesStatus = useAppSelector(preferencesSelectors.selectPreferencesStatus);
  const preferenceLoading = preferencesStatus === 'loading';
  const mixPrefTooltipFlag = useAppSelector((state) => selectOnboardingFlag(state, MIX_PREF_TOOLTIP_KEY));
  const hasSeenMixPrefTooltip = Boolean(mixPrefTooltipFlag?.seenAt);
  const recordingId = group.targetGroupId ?? group.id;
  const hasMovableTracks = group.tracks.some((track) => !track.isPast);
  const canDragGroup = isInteractive && hasMovableTracks && !group.isManual;

  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: `group:${group.id}`,
    data: { type: 'group', groupId: group.id, canDrag: canDragGroup } satisfies DragData,
    disabled: !isInteractive
  });

  const style: CSSProperties = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition: transition ?? undefined
  };

  const metrics: string[] = [];
  const trackCount = Number.isFinite(group.trackCount) ? group.trackCount : group.tracks.length;
  if (trackCount > 0) {
    metrics.push(`${trackCount} ${trackCount === 1 ? 'track' : 'tracks'}`);
  }
  if (group.durationLabel) {
    metrics.push(group.durationLabel);
  }
  if (group.releaseYear) {
    metrics.push(String(group.releaseYear));
  }

  const titleSource = group.workName ?? group.label ?? 'Recording';
  const composer = abbreviateComposerName(group.composerName);
  const titleText = composer ? `${composer}${titleSource ? `: ${titleSource}` : ''}` : titleSource;
  const catalogLabel = formatCatalogKey(group.catalogKey);
  const baseTitle = titleText ?? 'Recording';
  const titleWithCatalog = catalogLabel ? `${baseTitle}, ${catalogLabel}` : baseTitle;
  const groupTargetId = group.targetGroupId ?? group.id;
  const groupRemovalPending = pendingRemoval?.type === 'group' && pendingRemoval.id === groupTargetId;
  const showGroupRemove = group.hasRemovableTracks && !group.isManual;
  const queueRemovalDisabled =
    !isInteractive || groupRemovalPending || (pendingRemoval != null && !groupRemovalPending);
  const isCurrentGroup = currentRecordingGroupId === group.id;
  const isNextGroup = nextRecordingGroupId === group.id;
  const moveNextPending = moveNextPendingGroupId === group.id;
  const canShowMoveNext =
    !group.isManual && isInteractive && hasMovableTracks && currentRecordingGroupId != null && !isCurrentGroup && !isNextGroup;
  const moveNextDisabled =
    queueRemovalDisabled || moveNextPending || (pendingRemoval != null && !moveNextPending);

  const dragHandleAttributes = canDragGroup ? attributes : {};
  const dragHandleListeners = canDragGroup ? (listeners as Record<string, any>) : undefined;

  const sortableTrackIds = group.tracks.map((track) => `track:${track.id}`);
  const groupArtists = useMemo(() => deriveGroupArtists(group), [group]);

  const actionsRecording = useMemo<LibraryRecording>(() => {
    const baseId = group.targetGroupId ?? group.id;
    const candidate = group.tracks.find((track) => !track.isPast) ?? group.tracks[0] ?? null;
    const metadataRecord = candidate ? (candidate.metadata as Record<string, unknown>) : undefined;

    const recordingUri = (() => {
      if (!metadataRecord) return '';
      const raw = metadataRecord['recordingUri'];
      return typeof raw === 'string' ? raw : '';
    })();

    const spotifyUriFromMetadata = (() => {
      if (!metadataRecord) return undefined;
      const raw = metadataRecord['spotifyUri'];
      return typeof raw === 'string' ? raw : undefined;
    })();

    const workIdFromMetadata = (() => {
      if (!metadataRecord) return undefined;
      const raw = metadataRecord['workId'];
      return typeof raw === 'string' ? raw : undefined;
    })();

    const composerIdFromMetadata = (() => {
      if (!metadataRecord) return undefined;
      const raw = metadataRecord['composerId'];
      return typeof raw === 'string' ? raw : undefined;
    })();

    return {
      id: baseId,
      uri: recordingUri,
      type: 'recording',
      name: titleWithCatalog,
      performerName: group.performerName ?? '',
      workName: group.workName ?? '',
      composerName: group.composerName ?? '',
      albumId: baseId,
      albumName: group.albumName ?? titleWithCatalog,
      albumImageUrl: group.albumImageUrl ?? undefined,
      imageUrl: group.albumImageUrl ?? undefined,
      durationLabel: group.durationLabel ?? undefined,
      year: group.releaseYear != null ? String(group.releaseYear) : undefined,
      labels: undefined,
      notes: undefined,
      savedAt: undefined,
      trackCount: Number.isFinite(group.trackCount) ? group.trackCount : undefined,
      workCatalogLabel: group.catalogKey ?? undefined,
      workCatalogNumberNormalized: group.catalogKey ?? undefined,
      workId: workIdFromMetadata,
      composerId: composerIdFromMetadata,
      spotifyTrackId: candidate?.trackId ?? undefined,
      spotifyUri:
        spotifyUriFromMetadata ?? (candidate?.trackId ? `spotify:track:${candidate.trackId}` : undefined)
    };
  }, [group, titleWithCatalog]);

  const workId = actionsRecording.workId ?? null;
  const existingRecordingPreference = useAppSelector((state) =>
    preferencesSelectors.selectPreference(state, 'recording', recordingId)
  );
  const existingWorkPreference = useAppSelector((state) =>
    workId ? preferencesSelectors.selectPreference(state, 'work', workId) : undefined
  );
  const composerId = actionsRecording.composerId ?? null;
  const existingComposerPreference = useAppSelector((state) =>
    composerId ? preferencesSelectors.selectPreference(state, 'composer', composerId) : undefined
  );

  const [recordingPreferenceWeight, setRecordingPreferenceWeight] = useState<number>(
    clampPreferenceWeight(existingRecordingPreference?.weight ?? 1)
  );
  const [recordingPreferenceSaving, setRecordingPreferenceSaving] = useState(false);
  const [workPreferenceWeight, setWorkPreferenceWeight] = useState<number>(
    clampPreferenceWeight(existingWorkPreference?.weight ?? 1)
  );
  const [workPreferenceSaving, setWorkPreferenceSaving] = useState(false);
  const [composerPreferenceWeight, setComposerPreferenceWeight] = useState<number>(
    clampPreferenceWeight(existingComposerPreference?.weight ?? 1)
  );
  const [composerPreferenceSaving, setComposerPreferenceSaving] = useState(false);

  useEffect(() => {
    setRecordingPreferenceWeight(clampPreferenceWeight(existingRecordingPreference?.weight ?? 1));
  }, [existingRecordingPreference?.weight, recordingId]);

  useEffect(() => {
    if (!workId) return;
    setWorkPreferenceWeight(clampPreferenceWeight(existingWorkPreference?.weight ?? 1));
  }, [existingWorkPreference?.weight, workId]);

  useEffect(() => {
    if (!composerId) return;
    setComposerPreferenceWeight(clampPreferenceWeight(existingComposerPreference?.weight ?? 1));
  }, [existingComposerPreference?.weight, composerId]);

  const handleSaveRecordingPreference = useCallback(
    async (nextValue?: number) => {
      const normalizedInput =
        typeof nextValue === 'number' && Number.isFinite(nextValue)
          ? nextValue
          : recordingPreferenceWeight;
      const clamped = clampPreferenceWeight(normalizedInput);
      setRecordingPreferenceWeight(clamped);
      setRecordingPreferenceSaving(true);
      try {
        if (Math.abs(clamped - 1) < 0.0001) {
          await dispatch(
            removePreference({
              entityType: 'recording',
              entityId: recordingId
            })
          ).unwrap();
          setRecordingPreferenceWeight(1);
          message.success('Recording preference reset to neutral.');
          return;
        }

        const saved = await dispatch(
          upsertPreference({
            entityType: 'recording',
            entityId: recordingId,
            weight: clamped
          })
        ).unwrap();
        setRecordingPreferenceWeight(saved?.weight ?? clamped);
        message.success('Recording preference saved.');
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[Queue] failed to save recording preference', { recordingId, error });
        }
        message.error('Unable to save recording preference right now.');
      } finally {
        setRecordingPreferenceSaving(false);
      }
    },
    [dispatch, recordingId, recordingPreferenceWeight]
  );

  const handleSaveWorkPreference = useCallback(
    async (nextValue?: number) => {
      if (!workId) return;
      const normalizedInput =
        typeof nextValue === 'number' && Number.isFinite(nextValue)
          ? nextValue
          : workPreferenceWeight;
      const clamped = clampPreferenceWeight(normalizedInput);
      setWorkPreferenceWeight(clamped);
      setWorkPreferenceSaving(true);
      try {
        if (Math.abs(clamped - 1) < 0.0001) {
          await dispatch(
            removePreference({
              entityType: 'work',
              entityId: workId
            })
          ).unwrap();
          setWorkPreferenceWeight(1);
          message.success('Work preference reset to neutral.');
          return;
        }

        const saved = await dispatch(
          upsertPreference({
            entityType: 'work',
            entityId: workId,
            weight: clamped
          })
        ).unwrap();
        setWorkPreferenceWeight(saved?.weight ?? clamped);
        message.success('Work preference saved.');
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[Queue] failed to save work preference', { workId, error });
        }
        message.error('Unable to save work preference right now.');
      } finally {
        setWorkPreferenceSaving(false);
      }
    },
    [dispatch, workId, workPreferenceWeight]
  );

  const handleSaveComposerPreference = useCallback(
    async (nextValue?: number) => {
      if (!composerId) return;
      const normalizedInput =
        typeof nextValue === 'number' && Number.isFinite(nextValue)
          ? nextValue
          : composerPreferenceWeight;
      const clamped = clampPreferenceWeight(normalizedInput);
      setComposerPreferenceWeight(clamped);
      setComposerPreferenceSaving(true);
      try {
        if (Math.abs(clamped - 1) < 0.0001) {
          await dispatch(
            removePreference({
              entityType: 'composer',
              entityId: composerId
            })
          ).unwrap();
          setComposerPreferenceWeight(1);
          message.success('Composer preference reset to neutral.');
          return;
        }

        const saved = await dispatch(
          upsertPreference({
            entityType: 'composer',
            entityId: composerId,
            weight: clamped
          })
        ).unwrap();
        setComposerPreferenceWeight(saved?.weight ?? clamped);
        message.success('Composer preference saved.');
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[Queue] failed to save composer preference', { composerId, error });
        }
        message.error('Unable to save composer preference right now.');
      } finally {
        setComposerPreferenceSaving(false);
      }
    },
    [dispatch, composerId, composerPreferenceWeight]
  );

  const [showPreferenceTooltip, setShowPreferenceTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number; arrowOffset: number } | null>(null);
  const moreButtonRef = useRef<HTMLDivElement | null>(null);
  const markMixPrefSeenRef = useRef(false);

  const markMixPrefTooltipSeen = useCallback(() => {
    if (hasSeenMixPrefTooltip || markMixPrefSeenRef.current) return;
    markMixPrefSeenRef.current = true;
    dispatch(
      onboardingActions.markOnboardingSeen({
        key: MIX_PREF_TOOLTIP_KEY,
        version: mixPrefTooltipFlag?.version ?? 1
      })
    );
  }, [dispatch, hasSeenMixPrefTooltip, mixPrefTooltipFlag?.version]);

  const headerClassName = classNames('queue-recording-header', {
    'queue-recording-header--active': group.hasCurrentTrack,
    'queue-recording-header--dragging': isDragging
  });

  const actionButtonsClassName = classNames('queue-recording-header__action-buttons', {
    'queue-recording-header__action-buttons--visible': groupRemovalPending || showPreferenceTooltip
  });

  const isGroupCurrent = group.hasCurrentTrack;
  const isGroupPlaying = isGroupCurrent && isPlaying;
  const playButtonLabel = isGroupCurrent
    ? isGroupPlaying
      ? `Pause ${titleWithCatalog}`
      : `Resume ${titleWithCatalog}`
    : `Play ${titleWithCatalog}`;
  const showPlayButton = isInteractive;

  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggleExpansion(group.id);
    }
  };

  const handlePlayButtonClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onPlayGroup(group);
  };

  const handlePlayButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (!isGroupCurrent) {
      setShowPreferenceTooltip(false);
      return;
    }

    if (!isGroupPlaying) return;
    if (hasSeenMixPrefTooltip) return;

    const timerId = window.setTimeout(() => {
      setShowPreferenceTooltip(true);
      markMixPrefTooltipSeen();
    }, 3000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [hasSeenMixPrefTooltip, isGroupCurrent, isGroupPlaying, markMixPrefTooltipSeen]);

  useEffect(() => {
    if (!showPreferenceTooltip || !moreButtonRef.current) {
      setTooltipPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!moreButtonRef.current) return;
      const rect = moreButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const rightMargin = 30;
      const tooltipMaxWidth = 320; // max-width from CSS
      const buttonCenterX = rect.left + rect.width / 2;
      
      // Calculate maximum left position to ensure 30px right margin
      // Tooltip uses translate(-50%), so left is the center point
      // For right edge to be at (viewportWidth - rightMargin):
      // left + (tooltipMaxWidth / 2) <= viewportWidth - rightMargin
      // left <= viewportWidth - rightMargin - (tooltipMaxWidth / 2)
      // Add extra buffer to account for actual width potentially being less than max
      const maxLeft = viewportWidth - rightMargin - (tooltipMaxWidth / 2) - 20;
      
      // Position tooltip: use button center, but don't exceed maxLeft
      // This ensures right edge is always at least 30px from viewport
      // Shift 10px to the right as requested, then additional 30px to the right (15px + 15px)
      const finalLeft = Math.min(buttonCenterX, maxLeft) + 10 + 30 + 25 + 8 + 10;
      
      // Calculate arrow offset: button center relative to tooltip center
      // Since tooltip uses translate(-50%), finalLeft is the tooltip center
      // Arrow should point to button center, so offset = buttonCenterX - finalLeft
      // Compensate for the 15px tooltip shift by adjusting arrow 15px left
      const arrowOffset = buttonCenterX - finalLeft;
      
      setTooltipPosition({
        top: rect.top - 16, // 16px gap above button (21px - 5px down)
        left: finalLeft,
        arrowOffset
      });
    };

    updatePosition();
    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const container = moreButtonRef.current;
      if (container && target && container.contains(target)) return;
      setShowPreferenceTooltip(false);
    };
    const timeoutId = window.setTimeout(() => setShowPreferenceTooltip(false), 10000);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPreferenceTooltip]);

  return (
    <>
      <li
        ref={setNodeRef}
        className={headerClassName}
        data-queue-recording-id={group.id}
        style={style}
      >
        <div className='queue-recording-header__inner'>
          <div
            className='queue-recording-header__button'
            role='button'
            tabIndex={0}
            aria-expanded={isExpanded}
            onClick={() => onToggleExpansion(group.id)}
            onKeyDown={handleHeaderKeyDown}
          >
            <div className='queue-recording-header__art-wrapper'>
              <div
                className='queue-recording-header__art'
                style={group.albumImageUrl ? { backgroundImage: `url(${group.albumImageUrl})` } : undefined}
              />
              {showPlayButton ? (
                <button
                  type='button'
                  className='image-button queue-recording-header__play-button'
                  aria-label={playButtonLabel}
                  onClick={handlePlayButtonClick}
                  onKeyDown={handlePlayButtonKeyDown}
                >
                  {isGroupPlaying ? LibraryPauseGlyph : LibraryPlayGlyph}
                </button>
              ) : null}
            </div>
            <div className='queue-recording-header__content'>
              <div className='queue-recording-header__body'>
                <div
                  className='queue-recording-header__title'
                  title={titleWithCatalog}
                  aria-label={titleWithCatalog}
                >
                  {titleWithCatalog}
                </div>
              </div>
              <div className='queue-recording-header__meta-row'>
                {group.performerName ? (
                  <div className='queue-recording-header__performers' title={group.performerName}>
                    {group.performerName}
                  </div>
                ) : null}
                {metrics.length > 0 ? (
                  <div className='queue-recording-header__metrics'>{metrics.join(' • ')}</div>
                ) : null}
                <span className='queue-recording-header__chevron' aria-hidden>
                  {isExpanded ? '▴' : '▾'}
                </span>
              </div>
            </div>
          </div>
          <div className={actionButtonsClassName}>
            <RecordingActionsWrapper
              recording={actionsRecording}
              trigger={['click']}
              hideQueueAction
              hidePlaylistAction
              hideMuteAction
              artists={groupArtists}
              duplicateGoToArtistButton
              preferenceWeight={recordingPreferenceWeight}
              onPreferenceChange={setRecordingPreferenceWeight}
              onPreferenceSave={handleSaveRecordingPreference}
              preferenceDisabled={preferenceLoading || recordingPreferenceSaving}
              workPreferenceWeight={workPreferenceWeight}
              onWorkPreferenceChange={setWorkPreferenceWeight}
              onWorkPreferenceSave={handleSaveWorkPreference}
              workPreferenceDisabled={preferenceLoading || workPreferenceSaving}
              composerPreferenceWeight={composerPreferenceWeight}
              onComposerPreferenceChange={setComposerPreferenceWeight}
              onComposerPreferenceSave={handleSaveComposerPreference}
              composerPreferenceDisabled={preferenceLoading || composerPreferenceSaving}
              queueRemovalAction={
                showGroupRemove
                  ? {
                      onRemove: () => onRemoveGroup(group),
                      disabled: queueRemovalDisabled,
                      pending: groupRemovalPending
                    }
                  : undefined
              }
              queueMoveNextAction={
                canShowMoveNext
                  ? {
                      onMoveNext: () => onMoveGroupNext(group),
                      disabled: moveNextDisabled,
                      pending: moveNextPending
                    }
                  : undefined
              }
              hideTooltip={showPreferenceTooltip}
            >
              <div className='queue-recording-header__more-wrapper' ref={moreButtonRef}>
                <button
                  type='button'
                  className='queue-recording-header__more-button queue-recording-header__action-button'
                  aria-label={`More options for ${titleWithCatalog}`}
                >
                  <MenuDots />
                </button>
              </div>
              {showPreferenceTooltip && tooltipPosition && createPortal(
                <div
                  className={classNames('queue-pref-tooltip', { 'is-visible': showPreferenceTooltip })}
                  role='tooltip'
                  aria-hidden={!showPreferenceTooltip}
                  style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`
                  }}
                >
                  Adjust how often you hear this recording, work, performer, or composer in your mixes.
                  <div 
                    className='queue-pref-tooltip__arrow'
                    style={{
                      left: 'calc(50% + 94px)'
                    }}
                  />
                </div>,
                document.body
              )}
            </RecordingActionsWrapper>
          </div>
          <button
            type='button'
            className={classNames('queue-recording-header__drag-handle', {
              'queue-recording-header__drag-handle--disabled': !canDragGroup
            })}
            ref={setActivatorNodeRef}
            {...dragHandleAttributes}
            {...(dragHandleListeners ?? {})}
            aria-label={`Reorder ${titleWithCatalog}`}
            disabled={!canDragGroup}
            tabIndex={canDragGroup ? 0 : -1}
          >
            <GripHandleIcon />
          </button>
        </div>
      </li>
      {isExpanded ? (
        <>
          <SortableContext items={sortableTrackIds} strategy={verticalListSortingStrategy}>
            {group.tracks.map((track) => (
              <SortableQueueTrackItem
                key={track.id}
                entry={track}
                groupId={group.id}
                isInteractive={isInteractive}
                pendingRemoval={pendingRemoval}
                resolveItemId={resolveItemId}
                onRemove={onRemoveEntry}
                onSelect={onSelectEntry}
                actionsRecording={actionsRecording}
                artists={groupArtists}
                preferenceWeight={recordingPreferenceWeight}
                onPreferenceChange={setRecordingPreferenceWeight}
                onPreferenceSave={handleSaveRecordingPreference}
                preferenceDisabled={preferenceLoading || recordingPreferenceSaving}
                workPreferenceWeight={workPreferenceWeight}
                onWorkPreferenceChange={setWorkPreferenceWeight}
                onWorkPreferenceSave={handleSaveWorkPreference}
                workPreferenceDisabled={preferenceLoading || workPreferenceSaving}
                composerPreferenceWeight={composerPreferenceWeight}
                onComposerPreferenceChange={setComposerPreferenceWeight}
                onComposerPreferenceSave={handleSaveComposerPreference}
                composerPreferenceDisabled={preferenceLoading || composerPreferenceSaving}
                duplicateGoToArtistButton
                hideQueueAction
                hidePlaylistAction
                hideMuteAction
              />
            ))}
          </SortableContext>
        </>
      ) : null}
    </>
  );
};

type SortableManualQueueItemProps = {
  group: QueueRecordingGroup;
  isInteractive: boolean;
  pendingRemoval: PendingRemoval | null;
  resolveItemId: (entry: QueueListEntry) => string;
  onRemove: (entry: QueueListEntry) => void;
  onSelect: (index: number) => void;
};

const SortableManualQueueItem = ({
  group,
  isInteractive,
  pendingRemoval,
  resolveItemId,
  onRemove,
  onSelect
}: SortableManualQueueItemProps) => {
  const canDragGroup = isInteractive && group.hasRemovableTracks;
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: `group:${group.id}`,
    data: { type: 'group', groupId: group.id, canDrag: canDragGroup } satisfies DragData,
    disabled: !isInteractive
  });

  const entry = group.tracks[0];
  if (!entry) {
    return null;
  }

  const style: CSSProperties = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition: transition ?? undefined
  };

  const queueItemId = resolveItemId(entry);
  const removePending = pendingRemoval?.type === 'item' && pendingRemoval.id === queueItemId;
  const removeDisabled =
    !isInteractive || removePending || (pendingRemoval != null && !removePending);

  return (
    <QueueListItem
      ref={setNodeRef}
      entry={entry}
      disabled={!isInteractive}
      onSelect={onSelect}
      indent={false}
      onRemove={onRemove}
      removeDisabled={removeDisabled}
      removePending={removePending}
      style={style}
      dragHandleProps={{
        setActivatorNodeRef,
        attributes: canDragGroup ? attributes : undefined,
        listeners: canDragGroup ? (listeners as Record<string, any>) : undefined,
        disabled: !canDragGroup,
        hidden: true
      }}
      isDragging={isDragging}
    />
  );
};

type SortableQueueTrackItemProps = {
  entry: QueueListEntry;
  groupId: string;
  isInteractive: boolean;
  pendingRemoval: PendingRemoval | null;
  resolveItemId: (entry: QueueListEntry) => string;
  onRemove: (entry: QueueListEntry) => void;
  onSelect: (index: number) => void;
  actionsRecording: LibraryRecording;
  artists: Array<{ id: string; name?: string | null }>;
  preferenceWeight: number;
  onPreferenceChange: (next: number) => void;
  onPreferenceSave: (next?: number) => Promise<void> | void;
  preferenceDisabled: boolean;
  workPreferenceWeight: number;
  onWorkPreferenceChange: (next: number) => void;
  onWorkPreferenceSave: (next?: number) => Promise<void> | void;
  workPreferenceDisabled: boolean;
  composerPreferenceWeight: number;
  onComposerPreferenceChange: (next: number) => void;
  onComposerPreferenceSave: (next?: number) => Promise<void> | void;
  composerPreferenceDisabled: boolean;
  duplicateGoToArtistButton?: boolean;
  hideQueueAction?: boolean;
  hidePlaylistAction?: boolean;
  hideMuteAction?: boolean;
};

const SortableQueueTrackItem = ({
  entry,
  groupId,
  isInteractive,
  pendingRemoval,
  resolveItemId,
  onRemove,
  onSelect,
  actionsRecording,
  artists,
  preferenceWeight,
  onPreferenceChange,
  onPreferenceSave,
  preferenceDisabled,
  workPreferenceWeight,
  onWorkPreferenceChange,
  onWorkPreferenceSave,
  workPreferenceDisabled,
  composerPreferenceWeight,
  onComposerPreferenceChange,
  onComposerPreferenceSave,
  composerPreferenceDisabled,
  duplicateGoToArtistButton = false,
  hideQueueAction = false,
  hidePlaylistAction = false,
  hideMuteAction = false
}: SortableQueueTrackItemProps) => {
  const canDragTrack = isInteractive && !entry.isPast;

  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: `track:${entry.id}`,
    data: { type: 'track', trackId: entry.id, groupId, canDrag: canDragTrack } satisfies DragData,
    disabled: !isInteractive
  });

  const style: CSSProperties = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition: transition ?? undefined
  };

  const queueItemId = resolveItemId(entry);
  const removePending =
    pendingRemoval?.type === 'item' && pendingRemoval.id === queueItemId;
  const removeDisabled =
    !isInteractive || removePending || (pendingRemoval != null && !removePending);
  const canShowRecordingActions = entry.groupType === 'recording' || Boolean(entry.metadata.recordingId);

  const actionsNode = canShowRecordingActions ? (
    <RecordingActionsWrapper
      recording={actionsRecording}
      trigger={['click']}
      hideQueueAction={hideQueueAction}
      hidePlaylistAction={hidePlaylistAction}
      hideMuteAction={hideMuteAction}
      artists={artists}
      duplicateGoToArtistButton={duplicateGoToArtistButton}
      preferenceWeight={preferenceWeight}
      onPreferenceChange={onPreferenceChange}
      onPreferenceSave={onPreferenceSave}
      preferenceDisabled={preferenceDisabled}
      workPreferenceWeight={workPreferenceWeight}
      onWorkPreferenceChange={onWorkPreferenceChange}
      onWorkPreferenceSave={onWorkPreferenceSave}
      workPreferenceDisabled={workPreferenceDisabled}
      composerPreferenceWeight={composerPreferenceWeight}
      onComposerPreferenceChange={onComposerPreferenceChange}
      onComposerPreferenceSave={onComposerPreferenceSave}
      composerPreferenceDisabled={composerPreferenceDisabled}
      queueRemovalAction={{
        onRemove: () => {
          if (removeDisabled || removePending) return;
          onRemove(entry);
        },
        disabled: removeDisabled,
        pending: removePending
      }}
    >
      <button
        type='button'
        className={classNames('queue-list__more-button', {
          'queue-list__more-button--pending': removePending,
        })}
        aria-label={`More options for ${entry.metadata.title ?? 'track'}`}
      >
        <MenuDots />
      </button>
    </RecordingActionsWrapper>
  ) : undefined;

  return (
    <QueueListItem
      ref={setNodeRef}
      entry={entry}
      disabled={!isInteractive}
      onSelect={onSelect}
      indent
      onRemove={onRemove}
      removeDisabled={removeDisabled}
      removePending={removePending}
      style={style}
      actionsNode={actionsNode}
      dragHandleProps={{
        setActivatorNodeRef,
        attributes: canDragTrack ? attributes : undefined,
        listeners: canDragTrack ? (listeners as Record<string, any>) : undefined,
        disabled: !canDragTrack,
        hidden: true
      }}
      isDragging={isDragging}
    />
  );
};

type QueueDragOverlayProps =
  | { kind: 'group'; group: QueueRecordingGroup }
  | { kind: 'track'; entry: QueueListEntry };

const QueueDragOverlay = (props: QueueDragOverlayProps) => {
  if (props.kind === 'group') {
    const { group } = props;
    const titleSource = group.workName ?? group.label ?? 'Recording';
    const composer = abbreviateComposerName(group.composerName);
    const titleText = composer ? `${composer}${titleSource ? `: ${titleSource}` : ''}` : titleSource;
    return (
      <div className='queue-drag-overlay queue-drag-overlay--group'>
        <div className='queue-drag-overlay__title'>{titleText}</div>
        {group.performerName ? (
          <div className='queue-drag-overlay__meta'>{group.performerName}</div>
        ) : null}
      </div>
    );
  }

  const { entry } = props;
  return (
    <div className='queue-drag-overlay queue-drag-overlay--track'>
      <div className='queue-drag-overlay__title'>
        {entry.metadata.displayTitle ?? entry.metadata.title ?? `Track ${entry.position}`}
      </div>
      {entry.metadata.artists ? (
        <div className='queue-drag-overlay__meta'>{entry.metadata.artists}</div>
      ) : null}
    </div>
  );
};

export default Queue;
